import React, { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Link, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useThemeContext } from '../context/theme/context';

interface MembershipDeactivatedContentProps {
  isAdmin: boolean;
}

export const MembershipDeactivatedContent: React.FC<MembershipDeactivatedContentProps> = ({
  isAdmin,
}) => {
  const { logout } = useAuth0();
  const { brandLanguage } = useThemeContext();
  const { membership } = useEventTracking();
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true;
      membership.deactivatedScreenViewed({ is_admin: isAdmin });
    }
  }, [isAdmin, membership]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100%',
        pt: 6,
      }}
    >
      <Box
        sx={{
          maxWidth: 520,
          width: '100%',
          bgcolor: 'backgroundColors.secondary',
          border: 1,
          borderColor: 'borderColors.primary',
          borderRadius: '0.75rem',
          p: 3,
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
          Account Deactivated
        </Typography>

        <Typography sx={{ mb: 2 }}>
          Your {brandLanguage.brandName} account has been deactivated.
        </Typography>

        {isAdmin ? (
          <Typography sx={{ mb: 2 }}>
            As a practice administrator, please{' '}
            <Link href={`https://${brandLanguage.brandSite}/contact`} target="_blank">
              contact {brandLanguage.brandName} support
            </Link>{' '}
            to resolve this issue.
          </Typography>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>
              Please contact your practice administrator to reactivate your account.
            </Typography>
            <Typography sx={{ mb: 2 }}>
              If you believe this is an error, you can also{' '}
              <Link href={`https://${brandLanguage.brandSite}/contact`} target="_blank">
                contact {brandLanguage.brandName} support
              </Link>
              .
            </Typography>
          </>
        )}

        <Button variant="outlined" onClick={handleLogout} fullWidth sx={{ mt: 2 }}>
          Log Out
        </Button>
      </Box>
    </Box>
  );
};
